import React from 'react';
import Icon from './Icon';

/**
 * The hover state is a fill that sweeps in from the left rather than a colour
 * swap, which is what gives the buttons their weight. The sweep is a
 * pseudo-element behind the label, so the text never repaints mid-transition.
 */
const base =
  'group relative isolate inline-flex items-center justify-center gap-3 overflow-hidden whitespace-nowrap font-display text-xs font-medium uppercase tracking-[0.18em] transition-colors duration-300 sm:text-sm ' +
  'before:absolute before:inset-0 before:-z-10 before:origin-left before:scale-x-0 before:transition-transform before:duration-500 before:ease-out-expo hover:before:scale-x-100';

const sizes = {
  md: 'px-7 py-3.5',
  lg: 'px-9 py-4',
};

const variants = {
  gold: 'bg-gold-600 text-white before:bg-gold-700',
  dark: 'bg-ink text-cream before:bg-gold-600',
  outline: 'border border-ink/20 text-ink before:bg-ink hover:border-ink hover:text-cream',
  // For placement on dark sections, where the ink outline would disappear.
  'outline-light': 'border border-cream/30 text-cream before:bg-gold-600 hover:border-gold-600 hover:text-white',
};

/**
 * Renders an <a> when `href` is given, otherwise a <button>. Everything on this
 * page is an anchor today, but the contact and donate actions will become real
 * buttons once they are wired to a backend.
 */
export default function Button({
  as,
  href,
  variant = 'gold',
  size = 'md',
  arrow = false,
  className = '',
  children,
  ...rest
}) {
  const Tag = as || (href ? 'a' : 'button');
  const classes = `${base} ${sizes[size]} ${variants[variant]} ${className}`.trim();

  return (
    <Tag href={href} className={classes} {...rest}>
      {children}
      {arrow && (
        <Icon
          name="arrowRight"
          className="h-4 w-4 transition-transform duration-500 ease-out-expo group-hover:translate-x-1.5"
        />
      )}
    </Tag>
  );
}
